import { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from "react";
import {
  FaReply,
  FaTrash,
  FaBan,
  FaInbox,
  FaSpinner,
  FaEnvelopeOpen,
  FaEnvelope,
  FaPaperclip,
  FaDownload,
  FaCopy,
  FaFile,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileArchive,
  FaFileVideo,
  FaFileAudio,
  FaImage,
  FaExclamationTriangle,
  FaShieldAlt,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import * as mailService from "../../services/mailService";
import LoadingScreen from "../ui/temp-screens/LoadingScreen";

const IsolatedEmailFrame = forwardRef(({ html, flaggedLinks = [] }, ref) => {
  const iframeRef = useRef(null);

  useImperativeHandle(ref, () => ({
    restoreExternalImages: () => {
      const iframe = iframeRef.current;
      if (!iframe) return;
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      doc.querySelectorAll("img[data-original-src]").forEach((img) => {
        img.setAttribute("src", img.getAttribute("data-original-src"));
        img.removeAttribute("data-original-src");
      });
      doc.querySelectorAll("img[data-original-srcset]").forEach((img) => {
        img.setAttribute("srcset", img.getAttribute("data-original-srcset"));
        img.removeAttribute("data-original-srcset");
      });
      doc.querySelectorAll("[data-original-style]").forEach((el) => {
        el.setAttribute("style", el.getAttribute("data-original-style"));
        el.removeAttribute("data-original-style");
      });
      doc.querySelectorAll("link[data-original-href]").forEach((link) => {
        link.setAttribute("href", link.getAttribute("data-original-href"));
        link.removeAttribute("data-original-href");
      });

      setTimeout(() => {
        try {
          iframe.style.height = doc.documentElement.scrollHeight + "px";
        } catch {}
      }, 300);
    },
  }));

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    const flaggedStyle = flaggedLinks.length > 0 ? `
  a[data-flagged="high"] {
    color: #dc2626 !important;
    text-decoration: underline wavy #dc2626 !important;
    background: #fef2f2;
    padding: 1px 3px;
    border-radius: 2px;
    cursor: help;
    position: relative;
  }
  a[data-flagged="medium"] {
    color: #d97706 !important;
    text-decoration: underline wavy #d97706 !important;
    background: #fffbeb;
    padding: 1px 3px;
    border-radius: 2px;
    cursor: help;
  }
  a[data-flagged]::after {
    content: " ⚠";
    font-size: 10px;
  }` : "";

    doc.open();
    doc.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<style>
  html, body {
    margin: 0; padding: 0;
    font-family: Arial, sans-serif;
    font-size: 13px;
    color: #333;
    line-height: 1.6;
    word-break: break-word;
    overflow-wrap: break-word;
  }
  img { max-width: 100%; height: auto; }
  a { color: #ff6400; }
  ${flaggedStyle}
</style>
</head>
<body>${html}</body>
</html>`);
    doc.close();

    if (flaggedLinks.length > 0) {
      const flaggedHrefs = new Map(
        flaggedLinks.map((fl) => [fl.href, fl])
      );
      const anchors = doc.querySelectorAll("a[href]");
      anchors.forEach((a) => {
        const href = a.getAttribute("href");
        const match = flaggedHrefs.get(href);
        if (match) {
          a.setAttribute("data-flagged", match.riskLevel);
          a.setAttribute("title", `⚠ Enlace sospechoso: ${match.reasons.join("; ")}`);
        }
      });
    }

    const resize = () => {
      try {
        iframe.style.height = doc.documentElement.scrollHeight + "px";
      } catch {}
    };
    iframe.addEventListener("load", resize);
    setTimeout(resize, 100);
    return () => iframe.removeEventListener("load", resize);
  }, [html, flaggedLinks]);

  return (
    <iframe
      ref={iframeRef}
      title="email-content"
      className="w-full border-0"
      style={{ minHeight: "200px" }}
      sandbox="allow-same-origin allow-popups"
    />
  );
});

const formatFullDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("es-PE", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIconStyles = (att) => {
  const type = (att.contentType || "").toLowerCase();
  const name = (att.filename || "").toLowerCase();
  const styles = [
    { Icon: FaFile, bg: "bg-gray-100", icon: "text-gray-500" },
    { Icon: FaImage, bg: "bg-blue-50", icon: "text-blue-600" },
    { Icon: FaFilePdf, bg: "bg-red-50", icon: "text-red-600" },
    { Icon: FaFileWord, bg: "bg-blue-50", icon: "text-blue-700" },
    { Icon: FaFileExcel, bg: "bg-green-50", icon: "text-green-700" },
    { Icon: FaFileArchive, bg: "bg-amber-50", icon: "text-amber-600" },
    { Icon: FaFileVideo, bg: "bg-purple-50", icon: "text-purple-600" },
    { Icon: FaFileAudio, bg: "bg-pink-50", icon: "text-pink-600" },
  ];
  if (type.startsWith("image/")) return styles[1];
  if (type === "application/pdf" || name.endsWith(".pdf")) return styles[2];
  if (type.includes("word") || name.endsWith(".doc") || name.endsWith(".docx")) return styles[3];
  if (type.includes("excel") || type.includes("spreadsheet") || name.endsWith(".xls") || name.endsWith(".xlsx")) return styles[4];
  if (type.includes("zip") || type.includes("rar") || type.includes("compressed")) return styles[5];
  if (type.startsWith("video/")) return styles[6];
  if (type.startsWith("audio/")) return styles[7];
  return styles[0];
};

const RISK_CONFIG = {
  high: {
    bg: "bg-red-50",
    border: "border-red-200",
    icon: "text-red-600",
    title: "text-red-800",
    text: "text-red-700",
    badgeBg: "bg-red-100",
    badgeText: "text-red-700",
    badgeBorder: "border-red-200",
    linkBg: "bg-red-50",
    linkHover: "hover:bg-red-100",
  },
  medium: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    icon: "text-amber-600",
    title: "text-amber-800",
    text: "text-amber-700",
    badgeBg: "bg-amber-100",
    badgeText: "text-amber-700",
    badgeBorder: "border-amber-200",
    linkBg: "bg-amber-50",
    linkHover: "hover:bg-amber-100",
  },
};

const LinkAnalysisBanner = ({ linkAnalysis }) => {
  const [expanded, setExpanded] = useState(false);
  if (!linkAnalysis?.isSuspicious) return null;

  const risk = RISK_CONFIG[linkAnalysis.riskLevel] || RISK_CONFIG.medium;

  return (
    <div className={`mx-6 mt-4 rounded-lg border ${risk.border} ${risk.bg} overflow-hidden`}>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className={`flex-shrink-0 ${risk.icon}`}>
          <FaShieldAlt className="text-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-bold ${risk.title}`}>
            {linkAnalysis.riskLevel === "high"
              ? "Este correo contiene enlaces potencialmente peligrosos"
              : "Este correo contiene enlaces sospechosos"}
          </p>
          <p className={`text-[10px] mt-0.5 ${risk.text}`}>
            Se detectaron {linkAnalysis.flaggedLinks.length} enlace(s) con riesgo{" "}
            {linkAnalysis.riskLevel === "high" ? "alto" : "medio"}.
            {" "}Haz clic para ver detalles.
          </p>
        </div>
        <div className={`flex-shrink-0 ${risk.icon}`}>
          <FaExclamationTriangle className="text-sm" />
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          <div className="h-px bg-current opacity-10" />
          {linkAnalysis.flaggedLinks.map((link, i) => {
            const linkRisk = RISK_CONFIG[link.riskLevel] || RISK_CONFIG.medium;
            return (
              <div
                key={i}
                className={`rounded-md border ${linkRisk.badgeBorder} ${linkRisk.linkBg} p-3`}
              >
                <div className="flex items-start gap-2">
                  <FaExternalLinkAlt className={`text-[10px] mt-0.5 flex-shrink-0 ${linkRisk.badgeText}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className={`text-[10px] font-mono ${linkRisk.badgeText} break-all`}>
                        {link.href}
                      </code>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${linkRisk.badgeBg} ${linkRisk.badgeText}`}>
                        {link.riskLevel}
                      </span>
                    </div>
                    {link.displayText && link.displayText !== link.href && (
                      <p className="text-[10px] text-gray-500 mt-1">
                        Texto visible: <span className="font-medium">"{link.displayText}"</span>
                      </p>
                    )}
                    <ul className="mt-1.5 space-y-0.5">
                      {link.reasons.map((reason, j) => (
                        <li key={j} className={`text-[10px] ${linkRisk.text} flex items-start gap-1.5`}>
                          <span className="mt-[3px] w-1 h-1 rounded-full bg-current flex-shrink-0" />
                          {reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ExternalImagesBanner = ({ visible, onShowImages }) => {
  if (!visible) return null;

  return (
    <div className="mx-6 mt-3 flex items-center gap-3 px-4 py-2.5 rounded-lg border border-blue-200 bg-blue-50">
      <FaShieldAlt className="text-blue-500 text-sm flex-shrink-0" />
      <p className="text-[11px] text-blue-700 flex-1">
        Las imágenes externas fueron bloqueadas para proteger tu privacidad.
      </p>
      <button
        type="button"
        onClick={onShowImages}
        className="flex-shrink-0 px-3 py-1 text-[11px] font-semibold text-blue-600 bg-white border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
      >
        Mostrar imágenes
      </button>
    </div>
  );
};

const MessageViewer = ({ message, loading, onReply, onMarkUnread, onDelete, onMoveMessage, currentFolder = "INBOX", canDelete = true, mailAccountId, folder = "INBOX" }) => {
  const effectiveFolder = folder || currentFolder;
  const isInSpam = /spam|junk/i.test(effectiveFolder || "");
  const isInTrash = /trash|papelera|deleted|eliminad/i.test(effectiveFolder || "");
  const showReturnToInbox = isInSpam || isInTrash;
  const [downloadingPart, setDownloadingPart] = useState(null);
  const [imagesBlocked, setImagesBlocked] = useState(false);
  const emailFrameRef = useRef(null);

  useEffect(() => {
    setImagesBlocked(!!message?.hasExternalImages);
  }, [message?.uid, message?.hasExternalImages]);

  const handleShowImages = useCallback(() => {
    emailFrameRef.current?.restoreExternalImages();
    setImagesBlocked(false);
  }, []);

  const handleDownload = async (att) => {
    if (!mailAccountId || !message?.uid) return;
    setDownloadingPart(att.part);
    try {
      const blob = await mailService.getAttachmentBlob(mailAccountId, message.uid, att.part, folder);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename || "adjunto";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Error al descargar el adjunto");
      console.error(e);
    } finally {
      setDownloadingPart(null);
    }
  };

  if (loading) {
    return (
      <LoadingScreen text="Cargando mensaje..." />
    );
  }

  if (!message) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400">
        <FaEnvelopeOpen className="text-4xl mb-3 text-gray-300" />
        <p className="text-sm font-medium">Selecciona un mensaje</p>
        <p className="text-xs mt-1">Elige un correo de la bandeja para leerlo aquí</p>
      </div>
    );
  }

  const hasHtml = message.html && message.html.trim().length > 0;
  const hasText = message.text && message.text.trim().length > 0;

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-gray-900 mb-2">
              {message.subject || "(Sin asunto)"}
            </h2>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-gray-700">De:</span>
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  <span className="text-gray-600 truncate">{message.from}</span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(message.from || "");
                      toast.success("Copiado al portapapeles");
                    }}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex-shrink-0"
                    title="Copiar remitente"
                  >
                    <FaCopy className="text-[10px]" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-gray-700">Para:</span>
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  <span className="text-gray-600 truncate">{message.to}</span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(message.to || "");
                      toast.success("Copiado al portapapeles");
                    }}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex-shrink-0"
                    title="Copiar destinatario"
                  >
                    <FaCopy className="text-[10px]" />
                  </button>
                </div>
              </div>
              
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-gray-700">CC:</span>
                  <div className="flex items-center gap-1 min-w-0 flex-1">
                    <span className="text-gray-600 truncate">{message.cc}</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(message.cc || "");
                        toast.success("Copiado al portapapeles");
                      }}
                      className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex-shrink-0"
                      title="Copiar CC"
                    >
                      <FaCopy className="text-[10px]" />
                    </button>
                  </div>
                </div>
            
         
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-gray-700">CCO:</span>
                  <div className="flex items-center gap-1 min-w-0 flex-1">
                    <span className="text-gray-600 truncate">{message.bcc}</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(message.bcc || "");
                        toast.success("Copiado al portapapeles");
                      }}
                      className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex-shrink-0"
                      title="Copiar CCO"
                    >
                      <FaCopy className="text-[10px]" />
                    </button>
                  </div>
                </div>
          
              <div className="text-[10px] text-gray-400 mt-1">
                {formatFullDate(message.date)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => onReply(message)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
            >
              <FaReply className="text-[10px]" />
              Responder
            </button>
            {onMarkUnread && (
              <button
                onClick={onMarkUnread}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                title="Marcar como no leído"
              >
                <FaEnvelope className="text-[10px]" />
                Marcar como no leído
              </button>
            )}
            {onMoveMessage && (
              <button
                onClick={onMoveMessage}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${
                  showReturnToInbox ? "text-green-600 bg-green-50 hover:bg-green-100" : "text-amber-600 bg-amber-50 hover:bg-amber-100"
                }`}
                title={showReturnToInbox ? "Devolver a bandeja de entrada" : "Mover a spam"}
              >
                {showReturnToInbox ? <FaInbox className="text-[10px]" /> : <FaBan className="text-[10px]" />}
                {showReturnToInbox ? "Devolver a inbox" : "Mover a spam"}
              </button>
            )}
            {canDelete && !isInTrash && (
              <button
                onClick={() => onDelete(message.uid)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
              >
                <FaTrash className="text-[10px]" />
                Mover a papelera
              </button>
            )}
          </div>
        </div>
      </div>

      <LinkAnalysisBanner linkAnalysis={message.linkAnalysis} />
      <ExternalImagesBanner visible={imagesBlocked} onShowImages={handleShowImages} />

      <div className="flex-1 overflow-y-auto px-6 py-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {hasHtml ? (
          <IsolatedEmailFrame
            ref={emailFrameRef}
            html={message.html}
            flaggedLinks={message.linkAnalysis?.flaggedLinks || []}
          />
        ) : hasText ? (
          <pre className="text-sm text-gray-700 whitespace-pre-wrap font-[Poppins,sans-serif] leading-relaxed">
            {message.text}
          </pre>
        ) : (
          <p className="text-sm text-gray-400 italic">Este mensaje no tiene contenido.</p>
        )}

        {message.attachments?.length > 0 && (
          <div className="mt-6 pt-5 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-md bg-orange-50 flex items-center justify-center">
                <FaPaperclip className="text-orange-500 text-xs" />
              </div>
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Archivos adjuntos
              </span>
              <span className="text-[10px] text-gray-400 font-medium">
                {message.attachments.length} {message.attachments.length === 1 ? "archivo" : "archivos"}
              </span>
            </div>
            <div className="flex flex-wrap gap-4">
              {message.attachments.map((att) => {
                const { Icon, bg, icon } = getFileIconStyles(att);
                return (
                  <div
                    key={att.part}
                    className="flex flex-col items-center w-[200px] p-4 bg-white border border-gray-100 rounded-md shadow-sm hover:shadow-md hover:border-orange-100 transition-all duration-200"
                  >
                    <div className={`w-14 h-14 rounded-md ${bg} flex items-center justify-center mb-3 ${icon}`}>
                      <Icon className="text-xl" />
                    </div>
                    <span
                      className={`font-medium text-gray-800 text-[11px] w-full truncate  text-center ${att.size != null ? "mb-0.5" : "mb-3"}`}
                      title={att.filename}
                    >
                      {att.filename || `Parte ${att.part}`}
                    </span>
                    {att.size != null && (
                      <span className="text-[10px] text-gray-400 mb-3">{formatFileSize(att.size)}</span>
                    )}
                    <div className="flex items-center gap-2 w-full justify-center">
                      <button
                        type="button"
                        onClick={() => handleDownload(att)}
                        disabled={downloadingPart === att.part}
                        className="p-2 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors disabled:opacity-50"
                        title="Descargar archivo"
                      >
                        {downloadingPart === att.part ? (
                          <FaSpinner className="animate-spin text-xs" />
                        ) : (
                          <FaDownload className="text-xs" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageViewer;
